import { Substitution, SubstitutionMotive } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { isNotOccurringMotive } from '../../../Utils/substitution/notOccurringMotives';
import { TimeTableLessonBase } from './TimetablePeriod.interface';

// Placeholder display name the backend emits for persons it cannot resolve (Person.Empty).
const UNKNOWN_USER_PREFIX = 'Unknown User';

export const getKnownTeacherName = (name?: string | null): string | undefined =>
  name && !name.startsWith(UNKNOWN_USER_PREFIX) ? name : undefined;

/**
 * Returns true if the substitution states that the lesson does not take place.
 * See `isNotOccurringMotive` for which motives count as such.
 */
export const isNotOccurringLesson = (substitution: Substitution | null): boolean =>
  isNotOccurringMotive(substitution?.motive);

/**
 * Returns true if the given period is an exam.
 * Note: The exam info might also be set by the substitution info.
 */
export const isExam = (
  timeTableLesson: TimeTableLessonBase,
  substitution: Substitution | null,
): boolean => {
  if (timeTableLesson.exam) {
    return true;
  }

  if (substitution !== null) {
    return substitution.exam || !!substitution.motive?.includes(SubstitutionMotive.exam);
  }

  return false;
};

/**
 * Extracts and generates a list of human-readable reasons for a substitution lesson.
 */
export const getSubstitutionMotiveText = (substitution: Substitution, t: TFunction): string[] => {
  const allMotives = substitution.motive ?? [];
  const values: string[] = [];
  const substituteTeacher = getKnownTeacherName(substitution.substitutionLesson?.teacher);

  for (const motive of allMotives) {
    switch (motive) {
      //  Unterrichtsfrei
      case SubstitutionMotive.noLesson:
      // Entfällt
      // eslint-disable-next-line no-fallthrough
      case SubstitutionMotive.cancellation:
        values.push(
          t('timetable:period_info_row:cancellation', { defaultValue: 'Diese Stunde entfällt' }),
        );
        break;
      //  Betreuung
      case SubstitutionMotive.supervision:
        values.push(
          substituteTeacher
            ? t('timetable:period_info_row:supervisioned_by', {
                defaultValue: 'Betreuung durch {{teacher}}',
                teacher: substituteTeacher,
              })
            : t('timetable:period_info_row:supervision', { defaultValue: 'Betreuung' }),
        );
        break;
      // Wegverlegung
      case SubstitutionMotive.shiftedFrom:
      // Hinverlegung
      // eslint-disable-next-line no-fallthrough
      case SubstitutionMotive.shiftedTo:
        if (
          substitution.originLesson?.from &&
          // Sometimes units sets this motive, although the date hasn't changed
          substitution.originLesson?.from !== substitution.substitutionLesson?.from
        ) {
          values.push(
            t('timetable:period_info_row:shifted', {
              defaultValue: 'Verlegt von ursprünglich {{from}}',
              from: moment(substitution.originLesson?.from).format('ddd'),
            }),
          );
        }
        break;
      // Freisetzung
      case SubstitutionMotive.release:
        values.push(t('timetable:period_info_row:release', { defaultValue: 'Freisetzung' }));
        break;
      // Vertretung
      case SubstitutionMotive.standIn:
        values.push(
          substituteTeacher
            ? t('timetable:period_info_row:stand_in_by', {
                defaultValue: 'Vertretung durch {{teacher}}',
                teacher: substituteTeacher,
              })
            : t('timetable:period_info_row:stand_in', { defaultValue: 'Vertretung' }),
        );
        break;
      //  Raumwechsel
      case SubstitutionMotive.roomExchange:
        values.push(
          substitution.substitutionLesson?.room && substitution.originLesson?.room
            ? t('timetable:period_info_row:room_exchange_from', {
                defaultValue: 'Raumwechsel von {{from}} nach {{to}}',
                from: substitution.originLesson?.room,
                to: substitution.substitutionLesson.room,
              })
            : t('timetable:period_info_row:room_exchange', { defaultValue: 'Raumwechsel' }),
        );
        break;
      // Klausur
      case SubstitutionMotive.exam:
        values.push(t('timetable:period_info_row:exam', { defaultValue: 'Klausur' }));
        break;
      // Unbekannter Grund
      // eslint-disable-next-line unicorn/no-useless-switch-case
      case SubstitutionMotive.unknown:
      // eslint-disable-next-line no-fallthrough
      default:
        values.push(
          t('timetable:period_info_row:subtitution_motive_unknown', {
            defaultValue: 'Unbekannter Grund',
          }),
        );
        break;
    }
  }

  return values;
};
